/**
 * Server-side prefetch for builder-widget product data.
 *
 * Historically every product widget (sliders, trending, filter tabs, ...)
 * fetched its products FROM THE BROWSER after hydration — a home page with
 * 6-8 widgets fired 6-8 client API calls before it looked loaded.
 *
 * PageWrapper (a server component) now calls prefetchWidgets() so the
 * products are fetched during SSR (cached via fetch revalidate + the page's
 * own ISR cache) and arrive inside the initial HTML. The client hooks in
 * hooks/useLogicalProducts.ts treat this data as their initial state and
 * skip the client fetch entirely; if prefetch fails they fall back to the
 * old client-side behaviour.
 */

import { shuffleArray } from "@/utils";

const API_BASE = `${process.env.NEXT_PUBLIC_BASE_API}product/storefront`;
const REVALIDATE = Number(process.env.PAGE_DATA_REVALIDATE) || 300;

export interface WidgetPrefetch {
  products?: ProductItem[];
  left?: ProductItem[];
  main?: ProductItem[];
}

export async function fetchProductsByIds(
  ids: number[],
): Promise<ProductItem[]> {
  try {
    const res = await fetch(`${API_BASE}/get-by-ids`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
      next: { revalidate: REVALIDATE },
    });
    if (!res.ok) return [];
    const json: any = await res.json();
    return json?.data ?? [];
  } catch {
    return [];
  }
}

export async function fetchProductsByLogic(
  logical: LogicBuilder,
): Promise<ProductItem[]> {
  try {
    const res = await fetch(`${API_BASE}/get-by-logic`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(logical),
      next: { revalidate: REVALIDATE },
    });
    if (!res.ok) return [];

    const json: ProductItem[] = await res.json();
    if (
      !logical?.isFeatured &&
      (logical?.categoryOrder === "random" || logical?.brandOrder === "random")
    ) {
      shuffleArray(json);
    }
    return json ?? [];
  } catch {
    return [];
  }
}

/** Fetch for a {custom, logical} pair; undefined when the widget has no query. */
async function fetchPair(setting?: {
  custom?: { id: number }[];
  logical?: LogicBuilder;
}): Promise<ProductItem[] | undefined> {
  if (setting?.custom?.length) {
    return fetchProductsByIds(setting.custom.map((p) => p.id));
  }
  if (setting?.logical) {
    return fetchProductsByLogic(setting.logical);
  }
  return undefined;
}

async function prefetchWidget(
  widget: Widget,
): Promise<WidgetPrefetch | undefined> {
  const data: any = widget?.data;

  // matches the switch in PageWrapper's getBuilderElement (widget.name)
  switch (widget?.name) {
    case "trending-products": {
      const [left, main] = await Promise.all([
        fetchPair(data?.leftProducts),
        fetchPair(data?.mainProducts),
      ]);
      return { left, main };
    }

    case "filter-products":
      // first (default) tab only — other tabs load on click
      return { products: await fetchPair(data?.tabs?.[0]?.products) };

    case "populer-product":
    case "three-banner-products":
      return { products: await fetchPair(data?.setting) };

    case "single-product":
      return {
        products: await fetchPair({
          custom: data?.singleProductSlider?.products,
        }),
      };

    default:
      return undefined;
  }
}

/** Prefetch product data for every widget of a builder page, in parallel. */
export async function prefetchWidgets(
  widgets: Widget[],
): Promise<(WidgetPrefetch | undefined)[]> {
  return Promise.all(widgets.map((w) => prefetchWidget(w)));
}
